import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { deleteUser, sendOtp } from "../services/eventService";

const VerifyOtpPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // ✅ Get email
  const [email, setEmail] = useState("");

  useEffect(() => {
    const stateEmail = location.state?.email;
    const storedEmail = localStorage.getItem("deleteEmail");

    if (stateEmail) {
      setEmail(stateEmail);
      localStorage.setItem("deleteEmail", stateEmail);
    } else if (storedEmail) {
      setEmail(storedEmail);
    } else {
      navigate("/delete-account");
    }
  }, [location, navigate]);

  // ✅ Mask email
  const maskEmail = (email) => {
    if (!email) return "";
    const [name, domain] = email.split("@");
    if (!name || !domain) return email;

    const maskedName =
      name.length > 2
        ? name[0] + "****" + name[name.length - 1]
        : name[0] + "***";

    return `${maskedName}@${domain}`;
  };

  const maskedEmail = maskEmail(email);

  // ✅ OTP state
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const inputsRef = useRef([]);

  const handleChange = (value, index) => {
    if (!/^[0-9]?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  // ✅ Submit OTP
  const handleSubmit = async () => {
    if (loading) return;

    const finalOtp = otp.join("").trim();

    if (!/^\d{6}$/.test(finalOtp)) {
      alert("Please enter a valid 6-digit OTP");
      return;
    }

    if (!email) {
      alert("Email is missing");
      return;
    }

    try {
      setLoading(true);

      const res = await deleteUser(email, finalOtp);

      if (res?.status === 200 || res?.data?.success) {
        alert("OTP verified successfully");
        navigate("/final-delete");
      } else {
        alert(res?.data?.message || "Verification failed");
      }

    } catch (error) {
      console.error(error);

      if (error?.response?.status === 400) {
        alert("Invalid OTP");
      } else if (error?.response?.status === 404) {
        alert("User not found");
      } else if (error?.response?.status === 410) {
        alert("OTP expired");
      } else {
        alert(
          error?.response?.data?.message ||
          "Something went wrong. Please try again."
        );
      }

    } finally {
      setLoading(false);
    }
  };

  // ✅ Resend OTP
  const handleResend = async () => {
    if (!email || loading) return;

    try {
      setLoading(true);

      const res = await sendOtp(email);

      if (res?.status === 200) {
        if (res.data === "OTP sent successfully") {
          setOtp(["", "", "", "", "", ""]);

          // focus first box
          inputsRef.current[0]?.focus();

          alert("OTP resent successfully");
        } else {
          alert(res.data || "Failed to resend OTP");
        }
      } else {
        alert("Failed to send verification code");
      }

    } catch (error) {
      console.error(error);
      alert(
        error?.response?.data?.message ||
        "Something went wrong while resending OTP"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 px-4 py-6 flex flex-col items-center">

      {/* Breadcrumb */}
      <div className="w-full max-w-4xl mb-4 text-xs">
        <span className="text-neutral-400">Home / </span>
        <span className="text-black">Verify your Identity</span>
      </div>

      {/* Card */}
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-lg border border-black/10 p-6 sm:p-8">

        <h2 className="text-2xl sm:text-3xl font-semibold text-center mb-4">
          Verify your Identity
        </h2>

        <p className="text-center text-sm sm:text-base text-zinc-600 mb-6">
          Please enter the 6-digit code sent to your email{" "}
          <span className="font-bold">{maskedEmail}</span>.
        </p>

        {/* OTP */}
        <div className="w-full max-w-xs sm:max-w-sm mx-auto mb-6">
          <div className="grid grid-cols-6 gap-2 sm:gap-3">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputsRef.current[index] = el)}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="1"
                value={digit}
                disabled={loading}
                onChange={(e) => handleChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className="
                  w-full
                  aspect-square
                  text-center
                  text-base sm:text-lg md:text-xl
                  border border-neutral-400
                  rounded-lg
                  focus:outline-none focus:ring-2 focus:ring-green-600
                "
              />
            ))}
          </div>
        </div>

        {/* Resend */}
        <div className="flex justify-center items-center gap-2 mb-6 text-sm sm:text-base">
          <span className="text-stone-500">
            Didn’t receive the code?
          </span>
          <button
            onClick={handleResend}
            disabled={loading} // ✅ disable
            className="text-green-700 font-medium hover:underline"
          >
            {loading ? "Sending..." : "Resend Code"}
          </button>
        </div>

        {/* Delete */}
        <button
          onClick={handleSubmit}
          disabled={loading} // ✅ disable
          className="w-full h-12 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition"
        >
          {loading ? "Verifying..." : "Delete My Account"}
        </button>
      </div>
    </div>
  );
};

export default VerifyOtpPage;